/**
 * QuickEntryTab — INBOX stage
 * Domain selector, source class, institution, title, description, notes, minimal team
 * All fields auto-save via PATCH. Team members use dedicated researcher API.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ProjectDetailProject, ProjectResearcher } from '../../../types'
import { authFetchJson } from '../../../lib/authFetch'
import { DOMAIN_REGISTRY, type Domain } from '../../../lib/domainRegistry'
import { InlineNote } from '../../ui/InlineNote'
import { InlineText } from '../../ui/InlineText'
import { CompletableSection } from '../../ui/CompletableSection'
import type { SectionCompletionState } from '../../../hooks/useSectionCompletions'
import { SOURCE_CLASS_LABELS } from '@hos/shared/constants'
import { TeamSection } from '../../crm/TeamSection'

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string // 'biotech' | 'medtech' | 'deeptech' | 'shallowtech'
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
  /** Pre-loaded researchers from ProjectDetailResponse */
  initialResearchers?: ProjectResearcher[]
}

const DOMAINS = [
  { id: 'biotech', label: 'Biotech', color: '#E61B25' },
  { id: 'medtech', label: 'Medtech', color: '#3B82F6' },
  { id: 'deeptech', label: 'Deeptech', color: '#8B5CF6' },
  { id: 'shallowtech', label: 'Shallowtech', color: '#F59E0B' },
]

/** Institution type icons — used in autocomplete dropdown */
const INSTITUTION_ICONS: Record<string, string> = {
  university: '🎓',
  institute: '🔬',
  corporate: '🏢',
  hospital: '🏥',
  default: '🏛️',
}

function getInstitutionIcon(name: string): string {
  const lower = name.toLowerCase()
  if (lower.includes('uniwersytet') || lower.includes('university') || lower.includes('akademia')) return INSTITUTION_ICONS.university
  if (lower.includes('instytut') || lower.includes('institute') || lower.includes('centrum')) return INSTITUTION_ICONS.institute
  if (lower.includes('szpital') || lower.includes('hospital') || lower.includes('klinik')) return INSTITUTION_ICONS.hospital
  if (lower.includes('corp') || lower.includes('sp. z') || lower.includes('s.a.') || lower.includes('gmbh') || lower.includes('inc')) return INSTITUTION_ICONS.corporate
  return INSTITUTION_ICONS.default
}

// S9-hotfix4: SOURCE_CLASS options built from shared constants
const SOURCE_CLASSES = Object.entries(SOURCE_CLASS_LABELS).map(([value, label]) => ({
  value,
  label,
}))

export function QuickEntryTab({
  project,
  projectId,
  domain,
  onFieldChange,
  sectionCompletions: sc,
  tabId = 'tab-quick-entry',
  initialResearchers = [],
}: TabContentProps) {
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId, sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId, sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId, sectionId)

  // Team members state removed — now managed by TeamSection component
  // Initial researchers are still available for backward compat

  // ── Institution autocomplete ──
  const [institutions, setInstitutions] = useState<{ id: number; name: string; city: string | null }[]>([])
  const [instQuery, setInstQuery] = useState(project.institution?.name || '')
  const [instOpen, setInstOpen] = useState(false)
  const instRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_URL || ''
    fetch(`${API_BASE}/api/v2/projects/filter-options`)
      .then(r => r.json())
      .then(data => {
        if (data.institutions) setInstitutions(data.institutions)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (instRef.current && !instRef.current.contains(e.target as Node)) setInstOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredInstitutions = useMemo(() => {
    if (!instQuery.trim()) return institutions.slice(0, 10)
    const q = instQuery.toLowerCase()
    return institutions.filter(i => i.name.toLowerCase().includes(q)).slice(0, 10)
  }, [instQuery, institutions])

  // Domain is freely editable in early pipeline stages, locked after PICKED_FOR_REVIEW
  const DOMAIN_EDITABLE_STAGES = ['INBOX', 'LONG_LIST', 'PICKED_FOR_REVIEW']
  const canEditDomain = DOMAIN_EDITABLE_STAGES.includes(project.pipeline_stage?.toUpperCase() || 'INBOX')

  const handleDomainSelect = (domainId: string) => {
    if (!canEditDomain) return
    onFieldChange('domain', domainId)
    // Clear subdomain if switching domain (subdomain is domain-specific)
    if (domainId !== domain && project.subdomain) {
      onFieldChange('subdomain', null)
    }
  }

  const handleSubdomainSelect = (subId: string) => {
    if (!canEditDomain) return
    // Toggle off if clicking already-selected
    const next = project.subdomain === subId ? null : subId
    onFieldChange('subdomain', next)
  }

  const currentSubdomains = DOMAIN_REGISTRY[domain as Domain]?.subdomains ?? []

  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFieldChange('source_class', e.target.value)
  }

  const handleInstitutionSelect = (inst: { id: number; name: string }) => {
    setInstQuery(inst.name)
    setInstOpen(false)
    onFieldChange('org_unit_id', inst.id)
  }

  // Team API calls removed — now managed by TeamSection component

  // ── Pipeline Actions ──
  return (
    <div className="space-y-6">
      {/* Domain & Source Section */}
      <CompletableSection
        title="Domain & Source"
        sectionId="qe-domain-source"
        isComplete={isComplete('qe-domain-source')}
        completedByName={getInfo('qe-domain-source')?.completed_by_name}
        completedAt={getInfo('qe-domain-source')?.completed_at}
        onToggle={() => toggle('qe-domain-source')}
      >
        {/* Domain Selector — compact radio pills */}
        <div className="mb-5">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Domain <span className="text-[#E61B25]">*</span>
            {!canEditDomain && (
              <span className="ml-2 text-[11px] text-[#8b8ba0] font-normal">
                🔒 Locked after In Review — contact admin to change
              </span>
            )}
          </label>
          <div className="flex gap-2">
            {DOMAINS.map((d) => {
              const selected = domain === d.id
              return (
                <button
                  key={d.id}
                  onClick={() => handleDomainSelect(d.id)}
                  disabled={!canEditDomain}
                  className={`
                    flex items-center gap-2
                    px-4 py-2
                    rounded-full
                    text-[13px] font-medium
                    transition-all
                    ${!canEditDomain ? 'cursor-not-allowed opacity-60' : ''}
                  `}
                  style={{
                    backgroundColor: selected ? `${d.color}20` : '#12122a',
                    border: `2px solid ${selected ? d.color : '#2a2a44'}`,
                    color: selected ? d.color : '#8888a8',
                  }}
                >
                  <span className="text-[15px] leading-none flex-shrink-0">
                    {DOMAIN_REGISTRY[d.id as Domain]?.icon ?? ''}
                  </span>
                  {d.label}
                </button>
              )
            })}
          </div>

          {/* Subdomain pills — shown only for domains that declare subdomains */}
          {currentSubdomains.length > 0 && (
            <div className="mt-3 pl-4 border-l-2 border-[#2a2a44]">
              <label className="text-[12px] font-medium text-[#a8a8c0] block mb-2">
                Subdomain <span className="text-[11px] text-[#6a6a80] font-normal">(optional until Review)</span>
              </label>
              <div className="flex gap-2 flex-wrap">
                {currentSubdomains.map((sub) => {
                  const subSelected = project.subdomain === sub.id
                  const parentColor = DOMAINS.find(d => d.id === domain)?.color || '#8888a8'
                  return (
                    <button
                      key={sub.id}
                      onClick={() => handleSubdomainSelect(sub.id)}
                      disabled={!canEditDomain}
                      title={sub.description}
                      className={`
                        flex items-center gap-2
                        px-3 py-1.5
                        rounded-full
                        text-[12px] font-medium
                        transition-all
                        ${!canEditDomain ? 'cursor-not-allowed opacity-60' : ''}
                      `}
                      style={{
                        backgroundColor: subSelected ? `${parentColor}20` : '#12122a',
                        border: `1.5px solid ${subSelected ? parentColor : '#2a2a44'}`,
                        color: subSelected ? parentColor : '#8888a8',
                      }}
                    >
                      <span className="text-[13px] leading-none">{sub.icon}</span>
                      {sub.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Source Class */}
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Source Class
          </label>
          <select
            value={project.source_class || ''}
            onChange={handleSourceChange}
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          >
            <option value="">Select source class</option>
            {SOURCE_CLASSES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Institution — dictionary autocomplete */}
        <div ref={instRef} className="relative">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Institution
          </label>
          <input
            type="text"
            value={instQuery}
            onChange={(e) => {
              setInstQuery(e.target.value)
              setInstOpen(true)
            }}
            onFocus={() => setInstOpen(true)}
            placeholder="Search institution..."
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            "
          />
          {instOpen && filteredInstitutions.length > 0 && (
            <div className="
              absolute z-50 mt-1 w-full
              bg-[#1a1a32] border border-[#2a2a44]
              rounded-lg shadow-lg
              max-h-[200px] overflow-y-auto
            ">
              {filteredInstitutions.map((inst) => (
                <button
                  key={inst.id}
                  onClick={() => handleInstitutionSelect(inst)}
                  className="
                    w-full flex items-center gap-2
                    px-3 py-2 text-left
                    hover:bg-[#2a2a44]
                    transition-colors
                  "
                >
                  <span className="text-[14px] flex-shrink-0">{getInstitutionIcon(inst.name)}</span>
                  <div className="min-w-0">
                    <div className="text-[13px] text-[#e8e8f0] truncate">{inst.name}</div>
                    {inst.city && (
                      <div className="text-[11px] text-[#8b8ba0]">{inst.city}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </CompletableSection>

      {/* Quick Identification Section */}
      <CompletableSection
        title="Quick Identification"
        sectionId="qe-quick-identification"
        isComplete={isComplete('qe-quick-identification')}
        completedByName={getInfo('qe-quick-identification')?.completed_by_name}
        completedAt={getInfo('qe-quick-identification')?.completed_at}
        onToggle={() => toggle('qe-quick-identification')}
      >

        {/* Title — S9-hotfix6 (#3): inline filled-state, asterisk only when empty */}
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Project Title{!project.title?.trim() && <span className="text-[#E61B25]"> *</span>}
          </label>
          <InlineText
            value={project.title || ''}
            onChange={(value) => onFieldChange('title', value)}
            placeholder="Brief project name"
            emptyLabel="+ Add project title"
            required
            textClassName="text-[15px] font-medium text-[#e8e8f0]"
          />
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Description
          </label>
          <InlineNote
            value={project.description || ''}
            onChange={(value) => onFieldChange('description', value)}
            placeholder="What is this project about?"
            minHeight={80}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Notes
          </label>
          <InlineNote
            value={project.notes || ''}
            onChange={(value) => onFieldChange('notes', value)}
            placeholder="Any additional context or observations"
            minHeight={80}
          />
        </div>
      </CompletableSection>

      {/* Project Team Section — S10 Wave 2: CRM with people + orgs */}
      <TeamSection
        projectId={projectId}
        sectionCompletions={sc}
        tabId={tabId}
      />

      {/*
        S9-hotfix6: per-tab pipeline action panel removed.
        Stage transitions are now handled by the global ProjectActionBar
        rendered at the ProjectDetail level (visible from every tab).
      */}
    </div>
  )
}

export default QuickEntryTab
